import { DialogM } from "@common/components/alert/dialog/Dialog"
import { useInterval } from "@common/utils/useInterval"
import {
    apiIdle,
    apiRequest,
    vaporFileUpload,
    vaporFileUploadIdle,
} from "@data/redux/actions/api"
import { useCallback, useEffect, useRef, useState } from "react"
import { IS_WEB } from "@common/utils/mobileUtils"
import { useDispatch, useSelector } from "react-redux"
import { apiRequestWithPrerequest } from "../../data/redux/actions/api"
import { fetchResourceFromURI } from "@data/utility/fileFromURI"

export { fetchResourceFromURI, fileFromURI } from "@data/utility/fileFromURI"

export function useRequestsBatch(
    requestsBeforeFilesUpload = [],
    filesToUpload = [],
    formId,
    requestToSendFiles,
    requestsAfterFilesUpload = [],
    noIdle = false,
    withPrerequest = false
) {
    const vaporFileUploadService = useSelector(
        (state) => state.api?.vaporFileUpload
    )
    const dispatch = useDispatch()

    // Use ref to always access the latest requestToSendFiles to avoid stale closure
    const requestToSendFilesRef = useRef(requestToSendFiles)
    requestToSendFilesRef.current = requestToSendFiles

    const nbReqsBefore = requestsBeforeFilesUpload?.length
    const nbReqsAfter = requestsAfterFilesUpload?.length
    const requestsNumber =
        nbReqsBefore + nbReqsAfter + filesToUpload?.length + 1

    const apiRequestHandler = withPrerequest
        ? apiRequestWithPrerequest
        : apiRequest

    const [requestProgress, setRequestProgress] = useState(0)
    const [percentRequestCompleted, setPercentRequestCompleted] = useState(0)
    const [submitted, setSubmitted] = useState(false)
    const [currentRequest, setCurrentRequest] = useState(null)
    const [attachments, setAttachments] = useState([])
    const percentLimitForUpload = 0
    // Clamp to [0, 99] while in-flight: requestsNumber is recomputed each render
    // (depends on filesToUpload), so it can shrink mid-flight (e.g. images get
    // an id after upload success) while requestProgress doesn't decrease,
    // producing > 100% progress. The completion path resets to 0 instead of
    // ever rendering 100%, so capping at 99 is safe.
    const globalProgress = Math.min(
        99,
        Math.max(
            0,
            Math.round(
                ((requestProgress - 1 + percentRequestCompleted / 100) /
                    Math.max(requestsNumber, 1)) *
                    100
            )
        )
    )

    // The slot is shared by every sender of the service (the close-out card
    // saves its rating on the one its voice is sent on): only the answer to
    // the request this batch sent is ours. The reducer hands back the very
    // route params object that was dispatched, offline and 419 re-sends included.
    const currentRequestSlot = useSelector(
        (state) => state.api?.[currentRequest?.service?.name]
    )
    const isOurAnswer =
        !currentRequest?.params ||
        currentRequestSlot?.routeParams === currentRequest.params
    const currentRequestSuccess = isOurAnswer && !!currentRequestSlot?.success
    const currentRequestError =
        (isOurAnswer && currentRequestSlot?.error) || false
    const currentRequestSuccessData = currentRequestSlot?.data

    const setNewRequestStarted = () => {
        setSubmitted(true)
        setRequestProgress((x) => Math.min(x + 1, requestsNumber))
        setPercentRequestCompleted(0)
    }
    const setRequestsReset = () => {
        setSubmitted(false)
        setAttachments(null)
        setRequestProgress(0)
        setPercentRequestCompleted(0)
        setCurrentRequest(null)
    }

    const onUploadProgress = (progressEvent) => {
        setPercentRequestCompleted(
            Math.round(
                (progressEvent.loaded * percentLimitForUpload) /
                    progressEvent.total
            )
        )
    }

    useInterval(
        () => {
            setPercentRequestCompleted((p) => p + 1)
        },
        submitted &&
            percentRequestCompleted >= percentLimitForUpload &&
            percentRequestCompleted < 98
            ? 500
            : null
    )

    // Use ref to always access the latest nextRequest to avoid stale closure in startRequests
    const nextRequestRef = useRef(null)

    const startRequests = useCallback(() => {
        if (requestsNumber >= 1) nextRequestRef.current()
    }, [requestsNumber])

    useEffect(() => {
        if (currentRequestSuccess) {
            dispatch(apiIdle(currentRequest?.service))

            if (
                currentRequest?.onSuccessBeforeNext(
                    currentRequestSuccessData
                ) &&
                requestProgress < requestsNumber
            ) {
                nextRequest()
            } else {
                setRequestsReset()
            }
        }
    }, [currentRequestSuccess])

    useEffect(() => {
        if (
            vaporFileUploadService?.success &&
            vaporFileUploadService?.formId === formId &&
            requestProgress > 0
        ) {
            const responseKey = vaporFileUploadService?.response?.key
            // Capture the tmp key here — the one deterministic point where it's
            // known, scoped to this batch's formId, and BEFORE the idle reset
            // below. Watching the shared global vapor slot from elsewhere races
            // this reset and silently drops keys.
            requestToSendFilesRef.current?.onFileUploadSuccess?.(responseKey)
            dispatch(vaporFileUploadIdle())
            nextRequest([...attachments, responseKey])
        }
    }, [vaporFileUploadService?.success])

    const nextRequest = async (tmpData = []) => {
        if (tmpData) {
            setAttachments(tmpData)
        }
        if (requestProgress < nbReqsBefore) {
            const nextApiRequest = requestsBeforeFilesUpload[requestProgress]
            if (!nextApiRequest?.service) {
                setRequestsReset()
                return
            }
            dispatch(
                apiRequestHandler(
                    nextApiRequest.service,
                    nextApiRequest.params,
                    nextApiRequest.data,
                    {},
                    onUploadProgress
                )
            )
            setCurrentRequest(nextApiRequest)
            setNewRequestStarted()
        } else if (requestProgress < requestsNumber - nbReqsAfter - 1) {
            const attachment = filesToUpload[requestProgress - nbReqsBefore]
            if (!attachment?.uri) {
                setRequestsReset()
                return
            }
            let file
            try {
                file = await fetchResourceFromURI(attachment.uri)
            } catch (error) {
                // An unreadable local file (revoked `blob:`) never reaches the
                // upload: without this the batch would stay "submitted" forever.
                requestToSendFilesRef.current?.onError?.(error)
                setRequestsReset()
                return
            }

            dispatch(
                vaporFileUpload(
                    file,
                    { onUploadProgress, contentType: (IS_WEB && file?.type === 'text/html') ? (attachment?.type || file?.type) : file?.type },
                    formId
                )
            )
            setNewRequestStarted()
        } else if (requestProgress === requestsNumber - nbReqsAfter - 1) {
            // Use ref to get the latest requestToSendFiles to avoid stale closure
            const currentRequestToSendFiles = requestToSendFilesRef.current
            if (!currentRequestToSendFiles?.service) {
                setRequestsReset()
                return
            }
            const requestToSendFilesData = {
                ...currentRequestToSendFiles.data,
                [currentRequestToSendFiles.tmpFilesKey]: [
                    ...tmpData.map(currentRequestToSendFiles.tmpFilesFormatter),
                    ...currentRequestToSendFiles.additionalData,
                ],
            }
            dispatch(
                apiRequestHandler(
                    currentRequestToSendFiles.service,
                    currentRequestToSendFiles.params,
                    requestToSendFilesData,
                    {},
                    onUploadProgress
                )
            )
            setCurrentRequest(currentRequestToSendFiles)
            setNewRequestStarted()
        } else {
            // Guard against extra fires past the planned step count: when the
            // batch has no after-files requests, requestsAfterFilesUpload[idx]
            // would be undefined and we'd dispatch with an undefined service.
            const nextApiRequest =
                requestsAfterFilesUpload[
                    requestProgress - (nbReqsBefore + filesToUpload?.length + 1)
                ]
            if (!nextApiRequest?.service) {
                setRequestsReset()
                return
            }
            dispatch(
                apiRequestHandler(
                    nextApiRequest.service,
                    nextApiRequest.params,
                    nextApiRequest.data,
                    {},
                    onUploadProgress
                )
            )
            setCurrentRequest(nextApiRequest)
            setNewRequestStarted()
        }
    }
    nextRequestRef.current = nextRequest

    useEffect(() => {
        let errorMessage = null
        // During file uploads currentRequest is never set (only service steps
        // call setCurrentRequest), so a failed upload must fall back to the
        // files-request config for onError/showErrorDialog — without the
        // fallback, a lost S3 upload used to reset the batch with no signal
        // to the caller at all.
        const errorConfig = currentRequest ?? requestToSendFilesRef.current

        if (currentRequestError) {
            if (!noIdle) {
                errorMessage = `${currentRequestError?.message}`
                dispatch(apiIdle(currentRequest?.service))
            }
            // Call onError callback if provided
            currentRequest?.onError?.(currentRequestError)
            setRequestsReset()
        } else if (
            vaporFileUploadService?.error &&
            vaporFileUploadService?.formId === formId &&
            requestProgress > 0
        ) {
            // Same scoping as the success branch: the vapor slot is global,
            // so only handle a failure that belongs to this batch — otherwise
            // another screen's upload error would reset this batch and fire
            // its onError.
            if (!noIdle) {
                dispatch(vaporFileUploadIdle())
                errorMessage = `${vaporFileUploadService?.error?.message}`
            }
            // Call onError callback if provided
            errorConfig?.onError?.(vaporFileUploadService?.error)
            setRequestsReset()
        }

        if (errorMessage && errorConfig?.showErrorDialog)
            DialogM.show({
                text1: "Erreur",
                text2: errorMessage,
            })
    }, [currentRequestError, vaporFileUploadService?.error])

    return [globalProgress, submitted, startRequests]
}
